import type { SignalMarker } from './route-features.js';

export type SignalMode = 'hidden' | 'shown' | 'ping';

export interface SignalOverlay {
  setMap(map: google.maps.Map | null): void;
  setSignals(signals: SignalMarker[]): void;
  setMode(mode: SignalMode): void;
}

/**
 * Traffic lights drawn as plain elements in one overlay pane rather than as map
 * markers, so hundreds of them stay cheap and a ping can animate them with CSS.
 * The class is made after the Maps API has loaded, because it extends it.
 */
export function createSignalOverlay(maps: typeof google.maps): SignalOverlay {
  class Overlay extends maps.OverlayView implements SignalOverlay {
    private readonly layer = document.createElement('div');
    private signals: SignalMarker[] = [];
    private elements: HTMLDivElement[] = [];

    constructor() {
      super();
      this.layer.className = 'signal-layer';
      this.layer.dataset.mode = 'hidden';
    }

    override onAdd() {
      this.getPanes()?.overlayMouseTarget.appendChild(this.layer);
    }

    override onRemove() {
      this.layer.remove();
    }

    setSignals(signals: SignalMarker[]) {
      this.signals = signals;
      this.elements = signals.map(({ title }) => {
        const element = document.createElement('div');
        element.className = 'signal-light';
        element.title = title;
        maps.OverlayView.preventMapHitsAndGesturesFrom(element);
        return element;
      });
      this.layer.replaceChildren(...this.elements);
      this.draw();
    }

    setMode(mode: SignalMode) {
      this.layer.dataset.mode = mode;
      // Re-inserting the elements restarts the pulse when pinged again.
      if (mode === 'ping') this.layer.replaceChildren(...this.elements);
    }

    override draw() {
      const projection = this.getProjection();
      if (!projection) return;
      this.signals.forEach(({ lat, lng }, index) => {
        const pixel = projection.fromLatLngToDivPixel(new maps.LatLng(lat, lng));
        const element = this.elements[index]!;
        if (pixel) element.style.transform = `translate(${pixel.x}px, ${pixel.y}px)`;
      });
    }
  }
  return new Overlay();
}
